import type { ReactNode } from 'react';
import { CalendarClock } from 'lucide-react';
import { Link } from 'react-router';

import type { DueSoonItem } from './courseHomeUtils';
import styles from './DueSoonSection.module.css';

interface DueSoonSectionProps {
  items: DueSoonItem[];
}

interface DueSoonSummaryItem {
  dueAtMillis: number;
  dueLabel: string;
  href: string;
  id: string;
  subtitle: ReactNode;
  title: string;
}

function getQuestionCountLabel(questionCount: number) {
  return `${questionCount} question${questionCount === 1 ? '' : 's'}`;
}

// A question line reads "Homework · 5 questions"; a Kai Coach line names the
// session it is for, and opens the coach on it. Soonest first; a stable sort
// leaves entries due at the same moment in course order.
function getSummaryItems(items: DueSoonItem[]): DueSoonSummaryItem[] {
  const summaryItems = items.flatMap((item): DueSoonSummaryItem[] =>
    item.lines.map((line) =>
      line.kind === 'coachSession'
        ? {
            dueAtMillis: line.dueAtMillis,
            dueLabel: line.dueLabel,
            href: `${item.href}?thread=${encodeURIComponent(line.threadKey)}`,
            id: `${item.id}-${line.kind}-${line.entryKey}`,
            subtitle: line.sessionLabel,
            title: item.title,
          }
        : {
            dueAtMillis: line.dueAtMillis,
            dueLabel: line.dueLabel,
            href: item.href,
            id: `${item.id}-${line.kind}`,
            // The spaces sit outside the hidden dot: inside, they went with
            // it, and the name read "Homework5 questions" (#173).
            subtitle: (
              <>
                {line.typeLabel}{' '}
                <span aria-hidden="true">·</span>{' '}
                {getQuestionCountLabel(line.questionCount)}
              </>
            ),
            title: item.title,
          },
    ),
  );

  return summaryItems.sort((left, right) => left.dueAtMillis - right.dueAtMillis);
}

export function DueSoonSection({ items }: DueSoonSectionProps) {
  const summaryItems = getSummaryItems(items);

  return (
    <section aria-labelledby="due-soon-summary-heading" className={styles.card}>
      <div className={styles.header}>
        <CalendarClock aria-hidden="true" className={styles.icon} size={16} />
        <h2 className={styles.heading} id="due-soon-summary-heading">
          Due Soon Summary
        </h2>
      </div>

      {summaryItems.length > 0 ? (
        <ul className={styles.list}>
          {summaryItems.map((item) => (
            <li className={styles.item} key={item.id}>
              <Link className={styles.link} to={item.href}>
                <span className={styles.itemTitle}>{item.title}</span>
                <span className={styles.itemSubtitle}>{item.subtitle}</span>
                <span className={styles.dueLabel}>{item.dueLabel}</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.empty}>No upcoming assignments</p>
      )}
    </section>
  );
}
